import React from "react";
import { useTranslation } from "react-i18next";
import { IconChevronRight, IconEdit } from "@tabler/icons-react";

import { ParameterStatus } from "@/types/lab-insights";
import { computeRangeBarViewport } from "@/utils/lab-range-bar";
import styles from "./MLabParameterCard.module.css";

export interface MLabParameterCardProps {
  /** Top-row label text (e.g. group name on landing, formatted date on group details) */
  groupName: string;
  /** Color tone for the top label. "muted" matches the original group label, "indigo" matches the date variant. */
  topLabelTone?: "muted" | "indigo";
  parameterName: string;
  value: string | number | null;
  unit?: string | null;
  status: ParameterStatus;
  flagged?: boolean;
  rangeLow?: number | null;
  rangeHigh?: number | null;
  rangeText?: string | null;
  showEditIcon?: boolean;
  onEditClick?: () => void;
  /** Render a chevron-right at the end of the parameter name row (group-detail variant). */
  showChevron?: boolean;
  onCardClick?: () => void;
  className?: string;
  hideFlaggedChip?: boolean;
}

const dotClassForStatus = (status: ParameterStatus, flagged: boolean) => {
  if (flagged) return styles["statusDot--flagged"];
  if (status === ParameterStatus.NORMAL) return styles["statusDot--normal"];
  if (
    status === ParameterStatus.BORDERLINE_LOW ||
    status === ParameterStatus.BORDERLINE_HIGH
  ) {
    return styles["statusDot--flagged"];
  }
  return styles["statusDot--unknown"];
};

export const MLabParameterCard: React.FC<MLabParameterCardProps> = ({
  groupName,
  topLabelTone = "muted",
  parameterName,
  value,
  unit,
  status,
  flagged = false,
  rangeLow,
  rangeHigh,
  rangeText,
  showEditIcon = false,
  onEditClick,
  showChevron = false,
  onCardClick,
  className,
  hideFlaggedChip = false,
}) => {
  const { t } = useTranslation();

  const numericValue = typeof value === "number" ? value : Number(value);
  const viewport = computeRangeBarViewport(
    rangeLow ?? null,
    rangeHigh ?? null,
    Number.isFinite(numericValue) ? numericValue : null
  );

  const showIndicator =
    viewport !== null ||
    (rangeLow !== null && rangeLow !== undefined) ||
    (rangeHigh !== null && rangeHigh !== undefined) ||
    !!rangeText;

  const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onEditClick?.();
  };

  return (
    <div
      className={[
        styles.card,
        onCardClick ? styles.cardClickable : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      role={onCardClick ? "button" : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      onClick={onCardClick}
      onKeyDown={
        onCardClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onCardClick();
              }
            }
          : undefined
      }
    >
      <div className={styles.headerRow}>
        <span
          className={[
            styles.groupLabel,
            topLabelTone === "indigo" ? styles["groupLabel--indigo"] : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {groupName}
        </span>
        {showEditIcon && (
          <button
            type="button"
            className={styles.editIconButton}
            aria-label={t("common.edit")}
            onClick={handleEditClick}
          >
            <IconEdit size={20} stroke={1.5} />
          </button>
        )}
      </div>

      <div className={styles.paramNameRow}>
        <span className={styles.paramNameText}>
          <span
            className={[styles.statusDot, dotClassForStatus(status, flagged)]
              .filter(Boolean)
              .join(" ")}
            aria-hidden
          />
          <span className={styles.paramName} title={parameterName}>
            {parameterName}
          </span>
        </span>
        {showChevron && (
          <span className={styles.paramChevron} aria-hidden>
            <IconChevronRight size={16} stroke={2} />
          </span>
        )}
      </div>

      <div className={styles.valueRow}>
        <span
          className={[styles.value, flagged ? styles["value--flagged"] : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {value === null || value === undefined || value === "" ? "—" : value}
        </span>
        {unit && <span className={styles.unit}>{unit}</span>}
        {flagged && !hideFlaggedChip && (
          <span className={styles.flaggedChip}>{t("labInsights.flagged")}</span>
        )}
      </div>

      {showIndicator && (
        <div className={styles.indicator}>
          <div className={styles.indicatorBar}>
            {viewport && (
              <div
                className={styles.rangeArea}
                style={{
                  left: `${viewport.inRangeStartPct}%`,
                  right: `${100 - viewport.inRangeEndPct}%`,
                }}
              />
            )}
            {viewport?.valuePct !== null &&
              viewport?.valuePct !== undefined && (
                <span
                  className={styles.indicatorDot}
                  style={{ left: `${viewport.valuePct}%` }}
                  aria-hidden
                />
              )}
          </div>
          <div className={styles.indicatorValues}>
            {rangeLow !== null && rangeLow !== undefined && (
              <span
                className={styles.indicatorValue}
                style={
                  viewport && Number.isFinite(viewport.inRangeStartPct)
                    ? { left: `${viewport.inRangeStartPct}%` }
                    : { left: 0, transform: "none" }
                }
              >
                {rangeLow}
              </span>
            )}
            {rangeHigh !== null && rangeHigh !== undefined && (
              <span
                className={styles.indicatorValue}
                style={
                  viewport && Number.isFinite(viewport.inRangeEndPct)
                    ? { left: `${viewport.inRangeEndPct}%` }
                    : { left: "auto", right: 0, transform: "none" }
                }
              >
                {rangeHigh}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MLabParameterCard;
